/**
 * EmailRow Component
 *
 * Displays an email using the UniversalRow slot system.
 * Maps Email data to the appropriate slots - the UniversalRow
 * doesn't know it's rendering an email.
 *
 * Features:
 * - AI classification with M.E.A. (Marta Email Assistant)
 * - User can correct classifications to improve the model
 */

import React, { useMemo, useState } from 'react';
import { Mail, MailOpen, Brain, Loader2 } from 'lucide-react';
import { graphqlMutation } from '@business-assistant/shared/database/graphqlClient';
import {
  UniversalRow,
  FastActions,
  MetaSeparator,
  PrimaryAction,
  EntityAvatar,
  type FastActionType,
} from '../common/base';
import { ClassificationBadge, type ClassificationResult, type EmailClassification } from '../common/ClassificationBadge';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';

// =============================================================================
// TYPES
// =============================================================================

export interface Email {
  id: string; // eml-xxx
  from?: string;
  to?: string;
  subject: string;
  body?: string;
  direction: 'in' | 'out';
  at: string; // ISO date
  read?: boolean;
  hasAttachments?: boolean;
  /** IMAP folder (defaults to INBOX) */
  folder?: string;
  /** Pre-existing classification from backend (M.E.A. batch classification) */
  existingClassification?: EmailClassification;
  /** Confidence of pre-existing classification */
  existingConfidence?: number;
}

export interface EmailRowProps {
  entity: Email;
  onTitleClick?: (email: Email) => void;
  onActionClick?: (actionType: string, emailId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
  onClassificationCorrect?: (
    emailId: string,
    original: EmailClassification,
    corrected: EmailClassification
  ) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function EmailRow({
  entity: email,
  onTitleClick,
  onActionClick,
  onAvatarClick,
  onClassificationCorrect,
}: EmailRowProps) {
  const { t } = useTranslation();

  // Primary action state (archived)
  const [isArchived, setIsArchived] = useState(false);

  // AI Classification state
  const [isClassifying, setIsClassifying] = useState(false);
  const [classificationResult, setClassificationResult] = useState<ClassificationResult | null>(
    // Initialize with existing classification if available
    email.existingClassification
      ? {
          classification: email.existingClassification,
          confidence: email.existingConfidence || 0.8,
          reason: 'Classificato da M.E.A.',
        }
      : null
  );

  // Get linked action counts
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(email.id);
  }, [email.id]);

  // Get available actions
  const availableActions = useMemo(() => {
    return getAvailableActions(email.id) as FastActionType[];
  }, [email.id]);

  // Get contact name for avatar (sender for incoming, recipient for outgoing)
  const contactForAvatar = useMemo(() => {
    const contact = email.direction === 'in' ? email.from : email.to;
    if (!contact) return null;
    // Extract name from "Name <email>" format or use email
    const match = contact.match(/^([^<]+)\s*</);
    return match ? match[1].trim() : contact;
  }, [email]);

  // Classification error state
  const [classificationError, setClassificationError] = useState<string | null>(null);

  // Handle AI classification
  const handleClassify = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isClassifying || classificationResult) return;

    setIsClassifying(true);
    setClassificationError(null);

    try {
      console.log('[EmailRow] Classifying email:', { id: email.id, subject: email.subject, from: email.from });

      // Use classifyEmail which takes email content directly (no DB lookup needed)
      const data = await graphqlMutation<{ classifyEmail: {
        success: boolean;
        classification: string;
        confidence: number;
        reason: string;
        suggestedCompanyName?: string;
        error?: string;
      } }>(`
        mutation ClassifyEmail($input: ClassifyEmailInput!) {
          classifyEmail(input: $input) {
            success
            classification
            confidence
            reason
            suggestedCompanyName
            error
          }
        }
      `, {
        input: {
          subject: email.subject || '',
          body: email.body || email.subject || '',
          from: email.from || '',
        },
      });

      console.log('[EmailRow] Classification response:', data);

      const result = data.classifyEmail;
      if (!result?.success) {
        throw new Error(result?.error || 'Classification failed');
      }

      setClassificationResult({
        classification: (result.classification || 'unknown') as EmailClassification,
        confidence: result.confidence || 0,
        reason: result.reason || '',
        suggestedCompanyName: result.suggestedCompanyName || undefined,
      });
    } catch (error) {
      console.error('[EmailRow] Classification error:', error);
      setClassificationError(error instanceof Error ? error.message : 'Classification error');
    } finally {
      setIsClassifying(false);
    }
  };

  // Handle classification correction
  const handleCorrect = (
    emailId: string,
    original: EmailClassification,
    corrected: EmailClassification
  ) => {
    onClassificationCorrect?.(emailId, original, corrected);
  };

  // === SLOT: leftSlot (Primary Action + Avatar) ===
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="archive"
        active={isArchived}
        onChange={(archived) => {
          setIsArchived(archived);
          onActionClick?.('archive', email.id);
        }}
      />
      <EntityAvatar
        items={contactForAvatar ? { name: contactForAvatar } : []}
        type="auto"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // === SLOT: title (subject) ===
  const title = email.subject || t('emails.noSubject');

  // === SLOT: subtitle (from/to based on direction) ===
  const subtitle = useMemo(() => {
    const contact = email.direction === 'in' ? email.from : email.to;
    if (!contact) return undefined;
    const prefix = email.direction === 'in' ? t('emails.from') : t('emails.to');
    return `${prefix} ${contact}`;
  }, [email, t]);

  // === SLOT: icon (read/unread state) ===
  const icon = email.read
    ? <MailOpen size={16} color="#9ca3af" />
    : <Mail size={16} color="#3b82f6" />;

  // === SLOT: meta (preview + attachment indicator) ===
  const meta = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Body preview (truncated)
    if (email.body) {
      parts.push(
        <span
          key="preview"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '300px',
            display: 'inline-block',
          }}
        >
          {email.body.substring(0, 100)}
        </span>
      );
    }

    // Attachment indicator
    if (email.hasAttachments) {
      if (parts.length > 0) parts.push(<MetaSeparator key="sep" />);
      parts.push(<span key="attach">📎</span>);
    }

    return parts.length > 0 ? <>{parts}</> : null;
  }, [email.body, email.hasAttachments]);

  // === SLOT: status (unread = info dot) ===
  const status = !email.read ? 'info' as const : undefined;

  // Note: We don't use "done" for read emails - strikethrough is confusing for email UX
  // Only archived emails should appear as done

  // === SLOT: rightSlot (AI classify button/badge + fast actions) ===
  const aiClassifyElement = classificationResult ? (
    // Show ClassificationBadge with correction support
    <ClassificationBadge
      result={classificationResult}
      emailId={email.id}
      emailSubject={email.subject}
      emailFrom={email.from}
      emailBody={email.body}
      onCorrect={handleCorrect}
    />
  ) : classificationError ? (
    // Show error state with retry
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        console.log('[EmailRow] Retry button clicked');
        handleClassify(e);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        background: '#fef2f2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
      title={classificationError}
    >
      <Brain size={12} />
      <span>Riprova</span>
    </button>
  ) : (
    // Show classify button
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        console.log('[EmailRow] AI button clicked directly', { emailId: email.id, subject: email.subject });
        handleClassify(e);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      disabled={isClassifying}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        background: isClassifying ? '#f3f4f6' : '#f5f3ff',
        color: isClassifying ? '#9ca3af' : '#8b5cf6',
        border: 'none',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 500,
        cursor: isClassifying ? 'wait' : 'pointer',
        whiteSpace: 'nowrap',
      }}
      title={t('emails.classifyWithAI')}
    >
      {isClassifying ? (
        <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
      ) : (
        <>
          <Brain size={12} />
          <span>AI</span>
        </>
      )}
    </button>
  );

  const rightSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {aiClassifyElement}
      {availableActions.length > 0 && (
        <FastActions
          actions={availableActions}
          counts={linkedCounts as Partial<Record<FastActionType, number>>}
          onAction={(action) => onActionClick?.(action, email.id)}
        />
      )}
    </div>
  );

  return (
    <UniversalRow
      id={email.id}
      leftSlot={leftSlot}
      title={title}
      subtitle={subtitle}
      icon={icon}
      meta={meta}
      date={email.at}
      status={status}
      done={isArchived}
      rightSlot={rightSlot}
      onTitleClick={() => onTitleClick?.(email)}
    />
  );
}
